/**
 * Default paths toolbar — weight optimization (path-level / traffic distribution) + method + smart rotation.
 * Controls the same draft fields as the left-column “Traffic distribution AI (paths)” card.
 */

import type { FC } from 'react';
import { useId } from 'react';
import { HelpCircle } from 'lucide-react';
import type { VoluumFlowUpdate } from '@trafficpulse/shared';

import { Tooltip } from '@/components/ui/Tooltip';
import { Checkbox } from '@/components/ui/form/controls/Checkbox';
import { cn } from '@/lib/utils';

import {
  defaultPathsAutoOptimizedUniform,
  defaultPathsCalculationMethodUniform,
  defaultPathsOptimizationStats,
  setDefaultPathsAutoOptimized,
  setDefaultPathsCalculationMethod,
} from '../../../utils/default-paths-optimization';

import CalculationMethodSelect from './CalculationMethodSelect';

export interface DefaultPathsOptimizationRowProps {
  draft: VoluumFlowUpdate;
  onPatch: (mutator: (d: VoluumFlowUpdate) => void) => void;
  /** Hide the Weight-optimization control (render-only) when TrafficPulse
   *  Optimisation is active — it's auto-managed then. The draft/save payload
   *  is untouched, so nothing breaks. Smart rotation stays visible. */
  hideWeightOptimization?: boolean;
}

const DefaultPathsOptimizationRow: FC<DefaultPathsOptimizationRowProps> = ({
  draft,
  onPatch,
  hideWeightOptimization = false,
}) => {
  const weightId = useId();
  const smartId = useId();
  const { total } = defaultPathsOptimizationStats(draft);
  const disabled = total === 0;
  const autoU = defaultPathsAutoOptimizedUniform(draft);
  const methodU = defaultPathsCalculationMethodUniform(draft);
  const weightChecked = autoU === 'allOn';

  const weightLabel = (
    <label
      htmlFor={weightId}
      className="flex min-w-0 cursor-pointer items-baseline gap-1.5 text-[11px] leading-none"
    >
      <span className="truncate font-medium text-foreground">
        Weight optimization
      </span>
    </label>
  );

  return (
    <section
      aria-label="Default path weight optimization"
      className={cn(
        'space-y-1 rounded-md border border-border/60 bg-muted/15 px-2 py-1.5',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      {!hideWeightOptimization ? (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Checkbox
            id={weightId}
            className="h-3.5 w-3.5 shrink-0"
            checked={weightChecked}
            disabled={disabled}
            onCheckedChange={(state) =>
              onPatch((d) => setDefaultPathsAutoOptimized(d, state === true))
            }
            aria-label="Weight optimization for default paths"
          />
          <div className="min-w-0 flex-1">{weightLabel}</div>
        </div>
        {weightChecked ? (
          <div className="w-full pl-6">
            <CalculationMethodSelect
              className="w-full max-w-[14rem] [&_select]:max-w-none [&_select]:w-full"
              value={methodU}
              disabled={disabled}
              onChange={(v) =>
                onPatch((d) => setDefaultPathsCalculationMethod(d, v))
              }
            />
          </div>
        ) : null}
      </div>
      ) : null}
      <div className="flex items-center gap-2">
        <Checkbox
          id={smartId}
          className="h-3.5 w-3.5 shrink-0"
          checked={Boolean(draft.defaultPathsSmartRotation)}
          disabled={disabled}
          onCheckedChange={(v) =>
            onPatch((d) => {
              d.defaultPathsSmartRotation = v === true;
            })
          }
          aria-label="Smart rotation between default paths"
        />
        <label
          htmlFor={smartId}
          className="flex min-w-0 cursor-pointer items-center gap-1 text-[11px] font-medium text-foreground"
        >
          Smart rotation
          <Tooltip content="Flow-level setting from Voluum: rotate traffic across default paths.">
            <HelpCircle
              className="h-3 w-3 shrink-0 text-muted-foreground"
              aria-label="About smart rotation"
            />
          </Tooltip>
        </label>
      </div>
    </section>
  );
};

export default DefaultPathsOptimizationRow;
